/**
 * ============================================================
 * THE MUSE JEWELERS — App-Free Wishlist Engine  v2.1
 * File: assets/muse-wishlist.js
 *
 * Fixes in v2.1:
 *   - Storage key is now 'muse-wishlist' (hyphen) — matches
 *     what the heart buttons write to localStorage.
 *   - Search listener is attached AFTER products are rendered,
 *     so it always has cards to filter.
 *   - Heart button on product pages now has explicit inline
 *     styles as a fallback in case the CSS file is slow to load.
 *
 * Zero dependencies — no jQuery, no paid app.
 * ============================================================
 */

(function () {
  'use strict';

  /* ── Storage helpers ─────────────────────────────────────── */

  const STORAGE_KEY = 'muse-wishlist'; // hyphen — must match data-wishlist-toggle

  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  function isWishlisted(handle) {
    return getWishlist().includes(handle);
  }

  function addToWishlist(handle) {
    const list = getWishlist();
    if (!list.includes(handle)) {
      list.push(handle);
      saveWishlist(list);
    }
  }

  function removeFromWishlist(handle) {
    const list = getWishlist().filter(h => h !== handle);
    saveWishlist(list);
  }

  function toggleWishlist(handle) {
    if (isWishlisted(handle)) {
      removeFromWishlist(handle);
      return false;
    } else {
      addToWishlist(handle);
      return true;
    }
  }

  /* ── Badge counter ───────────────────────────────────────── */

  function updateBadge() {
    const count = getWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = count > 0 ? count : '';
      el.style.display = count > 0 ? 'inline-flex' : 'none';
    });
  }

  /* ── Toggle button state ─────────────────────────────────── */

  function setButtonState(btn, wishlisted) {
    if (!btn) return;
    btn.setAttribute('aria-pressed', wishlisted);
    btn.setAttribute('title', wishlisted ? 'Remove from Wishlist' : 'Add to Wishlist');
    btn.classList.toggle('is-wishlisted', wishlisted);

    // Inline style fallback — ensures the heart is always visible
    // even if muse-wishlist.css hasn't loaded yet
    btn.style.cssText = [
      'display:inline-flex',
      'align-items:center',
      'justify-content:center',
      'width:3.6rem',
      'height:3.6rem',
      'min-width:3.6rem',
      'border-radius:50%',
      'background:rgba(0,0,0,0.55)',
      'border:1px solid rgba(255,255,255,0.25)',
      'color:#fff',
      'cursor:pointer',
      'padding:0',
      'flex-shrink:0',
      'transition:background 0.2s,border-color 0.2s,transform 0.15s'
    ].join(';');

    const svg = btn.querySelector('svg');
    if (svg) {
      svg.style.cssText = [
        'width:1.8rem',
        'height:1.8rem',
        'fill:' + (wishlisted ? '#c9a84c' : 'none'),
        'stroke:' + (wishlisted ? '#c9a84c' : 'currentColor'),
        'stroke-width:1.5',
        'pointer-events:none'
      ].join(';');
    }
  }

  /* ── Initialise all wishlist toggle buttons on the page ──── */

  function initToggleButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const handle = btn.dataset.wishlistToggle;
      if (!handle) return;

      setButtonState(btn, isWishlisted(handle));

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        const added = toggleWishlist(handle);
        setButtonState(btn, added);
        updateBadge();
        showToast(added ? 'Added to your wishlist ♡' : 'Removed from your wishlist');
      });
    });
  }

  /* ── Toast notification ──────────────────────────────────── */

  function showToast(message) {
    let toast = document.getElementById('muse-wl-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'muse-wl-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      // Inline styles so toast works even without CSS file
      toast.style.cssText = [
        'position:fixed',
        'bottom:2.4rem',
        'left:50%',
        'transform:translateX(-50%) translateY(120%)',
        'background:#111',
        'border:1px solid #2a2a2a',
        'color:#fff',
        'font-size:1.3rem',
        'padding:1rem 2rem',
        'border-radius:4px',
        'z-index:9999',
        'white-space:nowrap',
        'transition:transform 0.3s ease',
        'pointer-events:none',
        'box-shadow:0 4px 20px rgba(0,0,0,0.5)'
      ].join(';');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.style.transform = 'translateX(-50%) translateY(0)';
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => {
      toast.style.transform = 'translateX(-50%) translateY(120%)';
    }, 2800);
  }

  /* ── Wishlist page renderer ──────────────────────────────── */

  async function renderWishlistPage() {
    const container = document.getElementById('muse-wishlist-container');
    if (!container) return;

    const handles = getWishlist();
    const emptyState = document.getElementById('muse-wishlist-empty');
    const grid = document.getElementById('muse-wishlist-grid');
    const searchInput = document.getElementById('muse-wishlist-search');

    if (handles.length === 0) {
      if (emptyState) emptyState.style.display = 'flex';
      if (grid) grid.style.display = 'none';
      if (searchInput) searchInput.closest('.muse-wl-search-wrap') &&
        (searchInput.closest('.muse-wl-search-wrap').style.display = 'none');
      return;
    }

    // Show search bar
    if (searchInput && searchInput.closest('.muse-wl-search-wrap')) {
      searchInput.closest('.muse-wl-search-wrap').style.display = 'block';
    }

    if (emptyState) emptyState.style.display = 'none';
    if (grid) {
      grid.style.display = 'grid';
      grid.innerHTML = '<div class="muse-wl-loading" style="grid-column:1/-1;text-align:center;padding:4rem;color:#aaa;">Loading your wishlist…</div>';
    }

    // Fetch all products in parallel
    const results = await Promise.allSettled(
      handles.map(handle =>
        fetch(`/products/${handle}.js`)
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      )
    );

    const products = results
      .map(r => r.status === 'fulfilled' ? r.value : null)
      .filter(Boolean);

    if (!grid) return;

    if (products.length === 0) {
      grid.innerHTML = '';
      grid.style.display = 'none';
      if (emptyState) emptyState.style.display = 'flex';
      return;
    }

    grid.innerHTML = products.map(p => buildCard(p)).join('');

    // Wire up remove buttons
    grid.querySelectorAll('[data-wishlist-remove]').forEach(btn => {
      btn.addEventListener('click', function () {
        const handle = this.dataset.wishlistRemove;
        removeFromWishlist(handle);
        updateBadge();
        const card = document.getElementById('muse-wl-card-' + handle);
        if (card) {
          card.style.opacity = '0';
          card.style.transform = 'scale(0.95)';
          setTimeout(() => {
            card.remove();
            const remaining = grid.querySelectorAll('.muse-wl-card').length;
            if (remaining === 0) {
              grid.style.display = 'none';
              if (emptyState) emptyState.style.display = 'flex';
              if (searchInput && searchInput.closest('.muse-wl-search-wrap')) {
                searchInput.closest('.muse-wl-search-wrap').style.display = 'none';
              }
            }
          }, 300);
        }
      });
    });

    // Wire up Add to Cart buttons
    grid.querySelectorAll('[data-wishlist-atc]').forEach(btn => {
      btn.addEventListener('click', async function () {
        const variantId = this.dataset.wishlistAtc;
        if (!variantId) return;
        this.disabled = true;
        this.textContent = 'Adding…';
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
          });
          if (res.ok) {
            this.textContent = 'Added!';
            showToast('Item added to your cart');
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          } else {
            this.textContent = 'Add to Cart';
            this.disabled = false;
          }
        } catch (err) {
          this.textContent = 'Add to Cart';
          this.disabled = false;
        }
      });
    });

    // ── Wire up search AFTER cards are in the DOM ─────────────
    if (searchInput) {
      // Clone to remove any stale listeners from previous renders
      const freshInput = searchInput.cloneNode(true);
      searchInput.parentNode.replaceChild(freshInput, searchInput);

      freshInput.addEventListener('input', function () {
        const q = this.value.trim().toLowerCase();
        grid.querySelectorAll('.muse-wl-card').forEach(card => {
          const title = (card.dataset.title || '').toLowerCase();
          card.style.display = title.includes(q) ? '' : 'none';
        });
      });
    }
  }

  /* ── Build a product card HTML string ────────────────────── */

  function buildCard(product) {
    const variant = product.variants[0];
    const available = variant && variant.available;
    const price = variant ? formatMoney(variant.price) : '';
    const comparePrice = (variant && variant.compare_at_price && variant.compare_at_price > variant.price)
      ? formatMoney(variant.compare_at_price) : null;
    const image = product.featured_image || '';
    const discount = comparePrice
      ? Math.round((1 - variant.price / variant.compare_at_price) * 100) : 0;

    return `
      <div class="muse-wl-card" id="muse-wl-card-${product.handle}" data-title="${escapeAttr(product.title)}"
           style="transition:opacity 0.3s ease,transform 0.3s ease;">
        <div class="muse-wl-card__img-wrap">
          <a href="${product.url}" aria-label="${escapeAttr(product.title)}">
            ${image
              ? `<img src="${image}" alt="${escapeAttr(product.title)}" loading="lazy" />`
              : `<div class="muse-wl-card__img-placeholder"></div>`}
          </a>
          ${discount > 0 ? `<span class="muse-wl-card__badge">${discount}% OFF</span>` : ''}
          <button class="muse-wl-card__remove" data-wishlist-remove="${product.handle}"
                  aria-label="Remove ${escapeAttr(product.title)} from wishlist" title="Remove">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="muse-wl-card__info">
          <a class="muse-wl-card__title" href="${product.url}">${escapeHtml(product.title)}</a>
          <div class="muse-wl-card__price">
            <span class="muse-wl-card__price-current">${price}</span>
            ${comparePrice ? `<span class="muse-wl-card__price-compare">${comparePrice}</span>` : ''}
          </div>
        </div>
        <div class="muse-wl-card__actions">
          ${available
            ? `<button class="muse-wl-card__atc" data-wishlist-atc="${variant.id}">Add to Cart</button>`
            : `<button class="muse-wl-card__atc muse-wl-card__atc--sold-out" disabled>Sold Out</button>`}
          <a class="muse-wl-card__view" href="${product.url}">View</a>
        </div>
      </div>`;
  }

  /* ── Utilities ───────────────────────────────────────────── */

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(str) {
    return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ── Boot ────────────────────────────────────────────────── */

  function init() {
    updateBadge();
    initToggleButtons();
    if (document.getElementById('muse-wishlist-container')) {
      renderWishlistPage();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose public API
  window.MuseWishlist = {
    add: addToWishlist,
    remove: removeFromWishlist,
    toggle: toggleWishlist,
    has: isWishlisted,
    getAll: getWishlist,
    refresh: renderWishlistPage
  };

})();
